/**
 * Records CRUD — list / fetch / create / soft-delete / restore.
 * Status transitions and submissions are handled in their own modules.
 */
import { supabase } from '@/lib/supabase/client'
import { searchRecordIds } from '@/features/searchable-fields/api/search-api'
import type { RecordRow, RecordsFilter } from './types'
import { DeleteForbiddenError } from './errors'

export async function fetchRecords(
  projectId: string,
  filter: RecordsFilter = {},
  page = 0,
  pageSize = 25,
) {
  // Search spans display_id + configured searchable field values (server-side
  // RPC). Resolve matching ids first, then constrain the list query so its
  // status/deleted filters, ordering and pagination all still apply.
  let searchIds: string[] | null = null
  if (filter.search) {
    searchIds = await searchRecordIds(projectId, filter.search)
    if (searchIds && searchIds.length === 0) {
      return { records: [], total: 0 }
    }
  }

  const sort = filter.sort ?? 'updated_desc'
  const orderBy: { column: string; ascending: boolean } =
    sort === 'created_desc' ? { column: 'created_at', ascending: false }
    : sort === 'created_asc' ? { column: 'created_at', ascending: true }
    : sort === 'display_id' ? { column: 'record_number', ascending: true }
    : { column: 'updated_at', ascending: false }

  let query = supabase
    .from('records')
    .select(
      `id, display_id, status, created_at, updated_at, submitted_at, approved_at,
       declined_at, is_deleted, created_by, owner_id, version, approval_comment`,
      { count: 'exact' },
    )
    .eq('project_id', projectId)
    .order(orderBy.column, { ascending: orderBy.ascending })
    .range(page * pageSize, (page + 1) * pageSize - 1)

  if (!filter.showDeleted) {
    query = query.eq('is_deleted', false)
  }

  if (filter.status && filter.status.length > 0) {
    query = query.in('status', filter.status)
  }

  if (searchIds) {
    query = query.in('id', searchIds)
  }

  const { data, error, count } = await query
  if (error) throw error

  const records = (data ?? []) as RecordRow[]

  // Resolve has_soft_warnings + has_form_change_revision via submissions roll-up
  if (records.length > 0) {
    const recordIds = records.map((r) => r.id)
    const [swRes, fcrRes] = await Promise.all([
      supabase
        .from('submissions')
        .select('record_id')
        .in('record_id', recordIds)
        .not('soft_warning_flags', 'eq', '[]'),
      supabase
        .from('submissions')
        .select('record_id')
        .in('record_id', recordIds)
        .eq('revision_reason', 'form_breaking_change')
        .eq('is_deleted', false),
    ])
    const withWarnings = new Set((swRes.data ?? []).map((r: { record_id: string }) => r.record_id))
    const withFormRevision = new Set((fcrRes.data ?? []).map((r: { record_id: string }) => r.record_id))

    for (const record of records) {
      record.has_soft_warnings = withWarnings.has(record.id)
      record.has_form_change_revision =
        record.status === 'needs_revision' && withFormRevision.has(record.id)
    }

    // hasSoftWarnings filter must be applied after the roll-up
    if (filter.hasSoftWarnings) {
      return { records: records.filter((r) => r.has_soft_warnings), total: count ?? 0 }
    }
  }

  return { records, total: count ?? 0 }
}

export async function fetchRecord(recordId: string) {
  const { data, error } = await supabase
    .from('records')
    .select('*')
    .eq('id', recordId)
    .single()
  if (error) throw error
  return data
}

export async function createRecord(projectId: string, userId: string) {
  const { data, error } = await supabase.rpc('create_record', {
    p_project_id: projectId,
    p_creator_id: userId,
  })
  if (error) throw error
  return data as { id: string; display_id: string }
}

/**
 * Soft delete / restore ride a plain UPDATE: the records_guard_deletion
 * trigger (migration 20260718090000) is the authorization and it also stamps
 * deleted_* / restored_* server-side — the client only flips the flag.
 * Denials come in two shapes and both become DeleteForbiddenError:
 *   - the guard trigger raises  → PostgREST error with a 'forbidden:' message
 *   - RLS matches no row        → 0-row update (hence .select() + count check)
 */
export async function softDeleteRecord(recordId: string, userId: string) {
  const { data, error } = await supabase
    .from('records')
    .update({ is_deleted: true, updated_by: userId })
    .eq('id', recordId)
    .select('id')
  if (error) {
    if (error.message?.includes('forbidden')) throw new DeleteForbiddenError('delete')
    throw error
  }
  if (!data || data.length === 0) throw new DeleteForbiddenError('delete')
}

export async function restoreRecord(recordId: string, userId: string) {
  const { data, error } = await supabase
    .from('records')
    .update({ is_deleted: false, updated_by: userId })
    .eq('id', recordId)
    .select('id')
  if (error) {
    if (error.message?.includes('forbidden')) throw new DeleteForbiddenError('restore')
    throw error
  }
  if (!data || data.length === 0) throw new DeleteForbiddenError('restore')
}
